import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { compactWaybillSettings } from '@hero/core';
import type { Id, OrgWaybillSettings, OrganizationProfile } from '@hero/schema';
import { useOrgProfileUpdateApi } from '@hero/api-hooks';
import { useWaybillHtmlPreview } from '@hero/web-hooks';
import { WaybillPreviewPanel } from '../Section/WaybillPreviewPanel';
import { WaybillSettingsForm } from '../Section/WaybillSettingsForm';

export interface WaybillSettingsManagerProps {
  profile: Partial<OrganizationProfile> | undefined;
  /** When set (admin CRM), preview/save targets this org. */
  previewOrgId?: Id;
  disabled?: boolean;
  isSubmitting?: boolean;
  onSaved?: () => void;
  /** Override save — admin passes `useUpdateOrgProfile` wrapper. */
  onSaveSettings?: (settings: OrgWaybillSettings | null) => Promise<void>;
}

export function WaybillSettingsManager({
  profile,
  previewOrgId,
  disabled,
  isSubmitting,
  onSaved,
  onSaveSettings,
}: WaybillSettingsManagerProps) {
  const { t, i18n } = useTranslation(['m-waybill']);
  const update = useOrgProfileUpdateApi();
  const [draft, setDraft] = useState<OrgWaybillSettings | undefined>();
  const saving = isSubmitting ?? update.isPending;

  const { html, isLoading, error } = useWaybillHtmlPreview({
    locale: i18n.language,
    previewOrgId,
    draft,
  });

  const handleDraftChange = useCallback((values: OrgWaybillSettings) => {
    setDraft(prev => {
      if (
        prev?.accent_color === values.accent_color &&
        prev?.use_org_logo === values.use_org_logo &&
        prev?.logo_shape === values.logo_shape &&
        prev?.logo_background === values.logo_background &&
        prev?.logo_size === values.logo_size &&
        prev?.logo_rounding === values.logo_rounding
      ) {
        return prev;
      }
      return values;
    });
  }, []);

  const handleSave = async (settings: OrgWaybillSettings) => {
    const compact = compactWaybillSettings(settings);

    try {
      if (onSaveSettings) {
        await onSaveSettings(compact);
      } else {
        await update.mutateAsync({ settings: { waybill_settings: compact } });
        toast.success(t('m-waybill:toast.success.settings_saved'));
      }
      setDraft(undefined);
      onSaved?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('m-waybill:preview.error'));
      throw e;
    }
  };

  const hasLogo = Boolean(profile?.logo?.url);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)] lg:items-start">
      <WaybillSettingsForm
        profile={profile}
        disabled={disabled}
        isSubmitting={saving}
        onSubmit={handleSave}
        onDraftChange={handleDraftChange}
      />
      <WaybillPreviewPanel html={html} isLoading={isLoading} error={error} hasLogo={hasLogo} />
    </div>
  );
}
